import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";

import actions from "actions";
import {
  getClinicianClientStatusInfoLoading,
  getClinicianWorkflowStatus,
  getUpdateUserInsuranceStatusLoading,
  getUser,
} from "selectors";
import { PageContainer } from "@answersnow/pkg-theme";
import { useGetClinicianBasicInfo } from "hooks/api/clinician/useGetClinicianBasicInfo";
import useGetClinicianClients from "hooks/api/clinician/useGetClinicianClients";

// Modules
import SummaryWeek from "./SummaryWeek";
import NextAppointment from "./NextAppointment/";
import AuthSchedDelivered from "./AuthSchedDelivered";
import CancellationsNoShows from "./CancellationsNoShows";
// import CaseloadSummary from "./CaseloadSummary";
import SessionUtilization from "./SessionUtilization";
import CallsNotStartedHandler from "../CallNotStartedHandler";
import CaseloadSummary from "./CaseloadSummary";

export default function ClinicianDashboardContainer() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Redux Selectors
  const clinicianWorkflowStatus = useSelector(getClinicianWorkflowStatus);
  const clinician = useSelector(getUser);
  const clientStatusInfoLoading = useSelector(getClinicianClientStatusInfoLoading);
  const updateUserInsuranceStatusLoading = useSelector(getUpdateUserInsuranceStatusLoading);

  // React Query Hooks
  const { clinicianBasicInfo, clinicianBasicInfoLoading } = useGetClinicianBasicInfo(
    clinician?.clinician_id
  );

  const { clinicianClients, clinicianClientsLoading } = useGetClinicianClients(clinician?.id, {
    size: 100,
  });

  useEffect(() => {
    dispatch(
      actions.setPageDetails({
        pageName: "Home",
        altTitle: " ",
        currentPage: "dashboard",
        menu: "clinicianHome",
        hideDivider: true,
      })
    );
  }, []);

  const loading =
    !clinicianWorkflowStatus ||
    clientStatusInfoLoading ||
    updateUserInsuranceStatusLoading ||
    clinicianBasicInfoLoading ||
    clinicianClientsLoading;

  return (
    <PageContainer loading={loading}>
      <Stack gap={7}>
        <Box id="clinician-dashboard-header">
          <Typography component="p" variant="body2" color="text.secondary">
            {new Date().toDateString()}
          </Typography>

          <Typography
            component="h2"
            variant="h2"
            fontFamily="TestTiemposText"
            fontWeight="500"
            color="#6C3693"
          >
            {clinician?.first_name && `Hey there, ${clinician.first_name} 👋`}
          </Typography>
        </Box>

        <Stack
          id="clinician-dashboard-content"
          gap={7}
          direction={{ xs: "column", xl: "row" }}
          flexWrap="wrap"
          sx={{
            "& > *": {
              width: { xs: "100%", md: "auto" },
              minWidth: 0,
              boxSizing: "border-box",
            },
          }}
        >
          <SummaryWeek
            clinicianId={clinician?.clinician_id}
            employmentType={clinicianBasicInfo?.employment_type}
            isSmallScreen={isSmallScreen}
          />

          <NextAppointment
            clinicianUserId={clinician?.id}
            clinicianInfo={clinicianBasicInfo}
            isSmallScreen={isSmallScreen}
          />
        </Stack>

        <AuthSchedDelivered
          clinicianId={clinician?.clinician_id}
          clients={clinicianClients}
          isSmallScreen={isSmallScreen}
        />

        <CancellationsNoShows
          clinicianId={clinician?.clinician_id}
          clients={clinicianClients}
          isSmallScreen={isSmallScreen}
        />

        <CaseloadSummary clinicianId={clinician?.clinician_id} isSmallScreen={isSmallScreen} />

        <SessionUtilization clinicianId={clinician?.clinician_id} isSmallScreen={isSmallScreen} />

        <CallsNotStartedHandler clinicianId={clinician?.clinician_id} />
      </Stack>
    </PageContainer>
  );
}
